import * as React from 'react'
import classnames from 'classnames'
import { Icon } from '@atoms'
import {
    ButtonProps,
    ButtonVariantName,
    ButtonSize,
    ButtonVariant,
} from './types'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { Spinner } from '../../common/Spinner/Spinner'

export const Button: React.FC<ButtonProps> = (props) => {
    const {
        name,
        children,
        icon,
        size: _size,
        variant: variantName = 'solidPrimary',
        fullWidth,
        onClick,
        disabled,
        isLoading,
        iconSize,
        className,
        iconClassName,
        active,
        buttonRef,
        ...passedProps
    } = props

    const [parent] = useAutoAnimate()
    const sizeName: ButtonSize = props.size ?? 'medium'
    const isIconButton = !children && icon

    const baseClassName = 'cursor-pointer focus:outline-4 rounded-lg transition'
    const alignClassName = icon ? 'text-left' : 'text-center'

    const buttonClassName = classnames(
        className,
        baseClassName,
        alignClassName,
        getVariantClassnames(variantName),
        isIconButton
            ? getIconSizeClassname(sizeName)
            : getSizeClassnames(sizeName),
        {
            'w-full': fullWidth,
            active,
        }
    )

    return (
        <button
            data-testid={name}
            onClick={onClick}
            className={buttonClassName}
            {...passedProps}
            disabled={disabled || isLoading}
            ref={buttonRef}
        >
            {icon && (
                <Icon
                    name={icon}
                    size={iconSize || 'small'}
                    className={classnames('block', iconClassName)}
                />
            )}
            {children && (
                <div
                    className="flex items-center justify-center gap-1 first:grow"
                    ref={parent}
                >
                    {children}
                    {isLoading && <Spinner size={16} width={3} />}
                </div>
            )}
        </button>
    )
}

const getVariantClassnames = (variantName: ButtonVariantName) => {
    const variants: Record<ButtonVariantName, ButtonVariant> = {
        solidPrimary: {
            default: 'bg-azure text-white',
            focus: 'focus:outline-blue-200 focus:bg-blue-600',
            hover: 'hover:bg-blue-600',
            active: '[&:not(:disabled)]:active:bg-blue-700',
            disabled:
                'disabled:bg-neutral-100 disabled:text-disabled disabled:cursor-default',
            selected: '',
        },
        outlinePrimary: {
            default:
                'bg-white text-neutral-900 shadow-inset-1 shadow-neutral-900',
            focus: 'focus:bg-white focus:text-black focus:outline-blue-200',
            hover: 'hover:bg-blue-50 hover:text-black hover:shadow-inset-1 hover:shadow-neutral-900',
            active: '[&:not(:disabled)]:active:bg-blue-100',
            disabled:
                'disabled:bg-white disabled:text-disabled disabled:cursor-default shadow-inset-1 !shadow-neutral-400',
            selected: '',
        },
        outlineSecondary: {
            default:
                'bg-white text-neutral-900 shadow-inset-1 shadow-neutral-300',
            focus: 'focus:bg-white focus:text-text-black focus:outline-blue-200',
            hover: 'hover:bg-blue-50 hover:text-text-black hover:shadow-inset-1 hover:shadow-blue-200',
            active: '[&:not(:disabled)]:active:bg-blue-100 [&:not(:disabled)]:active:shadow-inset-1 [&:not(:disabled)]:active:shadow-blue-200',
            disabled:
                'disabled:bg-white disabled:text-disabled disabled:cursor-default disabled:shadow-neutral-400',
            selected: '',
        },
        ghostPrimary: {
            default: 'bg-transparent text-blue-500',
            focus: 'focus:bg-white focus:text-blue-600 focus:outline-blue-200',
            hover: 'hover:text-blue-600 hover:bg-blue-50',
            active: '[&:not(:disabled)]:active:text-blue-600 [&:not(:disabled)]:active:bg-blue-100',
            disabled: 'disabled:text-disabled disabled:cursor-default',
            selected: '',
        },
        ghostSecondary: {
            default: 'bg-transparent text-neutral-900',
            focus: 'focus:bg-white focus:text-black focus:outline-blue-200',
            hover: 'hover:text-black hover:bg-blue-50',
            active: '[&:not(:disabled)]:active:text-black [&:not(:disabled)]:active:bg-blue-100',
            disabled: 'disabled:text-disabled disabled:cursor-default',
            selected: '[&.active]:bg-neutral-200 [&.active]:focus:outline-none',
        },
        ghostTertiary: {
            default: 'bg-white text-neutral-600',
            focus: 'focus:bg-white focus:text-neutral-600 focus:outline-blue-200',
            hover: 'hover:text-neutral-700 hover:bg-blue-50',
            active: '[&:not(:disabled)]:active:text-neutral-700 [&:not(:disabled)]:active:bg-blue-100',
            disabled: 'disabled:text-disabled disabled:cursor-default',
            selected: '',
        },
        group: {
            default:
                'text-neutral-800 border-neutral-300 border flex items-center justify-center [&:first-child]:border-r-0',
            focus: 'focus:outline-blue-200',
            hover: 'hover:bg-blue-50',
            active: '[&:not(:disabled)]:active:text-neutral-800 [&:not(:disabled)]:active:bg-neutral-100',
            disabled: 'disabled:text-neutral-300 disabled:cursor-default',
            selected: '',
        },
        solidPrimaryDestructive: {
            default: 'text-white bg-error',
            focus: 'focus:bg-red-600 focus:text-white focus:outline-red-200',
            hover: 'hover:bg-red-600 hover:text-white',
            active: '[&:not(:disabled)]:active:bg-red-700 [&:not(:disabled)]:active:text-white',
            disabled:
                'disabled:text-disabled disabled:shadow-neutral-200 disabled:bg-neutral-50 disabled:cursor-default',
            selected: '',
        },
        outlinePrimaryDestructive: {
            default: 'text-red-500 bg-white border border-red-500',
            focus: 'focus:bg-white focus:text-red-600 focus:border-red-500 focus:outline-red-600',
            hover: 'hover:bg-red-50 hover:text-red-600 hover:border-red-600',
            active: '[&:not(:disabled)]:active:bg-red-100 [&:not(:disabled)]:active:text-red-600 [&:not(:disabled)]:active:border-red-700',
            disabled:
                'disabled:bg-white disabled:text-disabled disabled:cursor-default disabled:border-neutral-400 disabled:shadow-none',
            selected: '',
        },
        ghostPrimaryDestructive: {
            default: 'text-red-500 bg-white',
            focus: 'focus:bg-white focus:text-red-600 focus:outline-red-200',
            hover: 'hover:bg-red-50 hover:text-red-600',
            active: '[&:not(:disabled)]:active:bg-red-100 [&:not(:disabled)]:active:text-red-600 ',
            disabled: 'disabled:text-disabled disabled:cursor-default',
            selected: '',
        },
    }

    return classnames(
        variants[variantName].default,
        variants[variantName].hover,
        variants[variantName].active,
        variants[variantName].disabled,
        variants[variantName].focus,
        variants[variantName].selected
    )
}

const getIconSizeClassname = (size: ButtonSize) => {
    const sizes: Record<string, string> = {
        xsmall: 'w-4 h-4 p-0 flex items-center justify-center', // 14px square
        small: 'w-6 h-6 p-0 flex items-center justify-center', // 16px square
        smedium: 'w-7 h-7 p-0 flex items-center justify-center', // 20px square
        medium: 'w-8 h-8 p-0 flex items-center justify-center', // 24px square
        large: 'w-10 h-10 p-0 flex items-center justify-center', // 32px square
        xlarge: 'w-12 h-12 p-0 flex items-center justify-center', // 40px square
    }
    return sizes[size]
}

const getSizeClassnames = (size: ButtonSize) => {
    const sizes: Record<ButtonSize, string> = {
        xsmall: 'px-1 py-0 text-xs h-4 !rounded',
        small: 'px-2 py-0 text-sm h-6 !rounded-md',
        medium: 'px-3 py-1 text-sm',
        large: 'px-3 py-2 text-sm',
        xlarge: 'px-4 py-3 text-base',
    }
    return classnames('flex items-center gap-1', sizes[size])
}
